"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";

/**
 * Tells the server this tab is alive, so the admin nav can show who is on the
 * site right now. Mounted once in the root layout.
 *
 * Deliberately cheap and silent: one small POST every 20s while the tab is
 * visible, nothing at all while it is hidden, and a `sendBeacon` on the way out
 * so the count drops the moment someone leaves rather than 45s later. Failures
 * are swallowed — a customer must never see anything because of this.
 */

const HEARTBEAT_MS = 20_000;
/** Admin and login are staff, not visitors; signing links carry a credential in the path. */
const IGNORED_PREFIXES = ["/admin", "/login", "/sign"];

/**
 * Kept in memory only, never in `localStorage` or a cookie: nothing is written
 * to the visitor's device, so the tracker needs no consent under ePrivacy. It
 * lives as long as the page load (client-side navigations keep it); a reload
 * gets a new one, and the old record is removed by the `pagehide` beacon.
 */
let pageLoadId: string | null = null;

/** Where earlier versions persisted the id; cleared so returning browsers don't keep it. */
const LEGACY_STORAGE_KEY = "ps-presence-id";

function visitorId(): string | null {
  if (pageLoadId) return pageLoadId;
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Blocked storage has nothing of ours in it anyway.
  }
  try {
    pageLoadId = crypto.randomUUID();
  } catch {
    // `randomUUID` needs a secure context; without it, skip tracking.
  }
  return pageLoadId;
}

export default function PresenceTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const eventId = searchParams.get("eventId") ?? searchParams.get("id");

  // The start of this tab's visit, used to show "on site for 4 min".
  const startedAt = useRef<number>(Date.now());

  const tracked = !IGNORED_PREFIXES.some((prefix) => pathname.startsWith(prefix));

  useEffect(() => {
    if (!tracked) return;

    const id = visitorId();
    if (!id) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const beat = () => {
      const body = JSON.stringify({
        id,
        path: pathname,
        eventId,
        sinceMs: Date.now() - startedAt.current,
        referrer: document.referrer || null,
        // Sent from the page because the `User-Agent` header that reaches the
        // route on App Hosting is not the visitor's.
        ua: navigator.userAgent,
      });
      void fetch("/api/presence", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    };

    const start = () => {
      if (timer) return;
      beat();
      timer = setInterval(beat, HEARTBEAT_MS);
    };

    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    const leave = () => {
      stop();
      const body = JSON.stringify({ id, leave: true });
      try {
        // `sendBeacon` survives the page going away; `fetch` may not.
        navigator.sendBeacon(
          "/api/presence",
          new Blob([body], { type: "application/json" })
        );
      } catch {
        // Nothing to do — the record ages out of the activity window anyway.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", leave);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", leave);
    };
  }, [tracked, pathname, eventId]);

  return null;
}
